import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMaterials } from '../hooks/useMaterials';
import MaterialDetail from '../components/materials/MaterialDetail';
import Loading from '../components/common/Loading';
import EmptyState from '../components/common/EmptyState';
import ErrorIcon from '@mui/icons-material/Error';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { useAppContext } from '../contexts/AppContext';
import { useLang } from '../contexts/langContext';
import CopilotLauncher from '../components/copilot/CopilotLauncher';
import CopilotPanel from '../components/copilot/CopilotPanel';
import React from 'react';

function MaterialDetailPage() {
  const { materialId } = useParams<{ materialId: string }>();
  const { currentMaterial, isLoading, error, getMaterialById, deleteMaterial, toggleMaterialEnabled } = useMaterials();
  const { setAppTitle } = useAppContext();
  const { lang } = useLang();
  const [copilotOpen, setCopilotOpen] = React.useState(false);

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'Material Detail' : '材料详情');
  }, []);

  useEffect(() => {
    if (materialId) {
      getMaterialById(materialId);
    }
  }, [materialId]);

  if (error) {
    // Check if it's a 404 error
    const is404 = error.message.includes('404') || error.message.toLowerCase().includes('not found');
    
    const errorMessage = is404 
      ? (lang === 'en' 
          ? 'Material not found. It might have been deleted or you don\'t have permission to view it.' 
          : '找不到材料。它可能已被删除或您没有查看权限。')
      : error.message;
    
    return (
      <EmptyState 
        icon={is404 ? SentimentDissatisfiedIcon : ErrorIcon}
        message={errorMessage}
        iconSize={72}
        sx={{ marginTop: 4 }}
      />
    );
  }

  if (isLoading || !currentMaterial) {
    return <Loading />;
  }

  const handleDelete = async (materialId: string) => {
    await deleteMaterial(materialId);
  };

  const handleToggleEnabled = async (materialId: string) => {
    await toggleMaterialEnabled(materialId);
  };

  return (
    <>
      <MaterialDetail
        material={currentMaterial}
        onDelete={handleDelete}
        onToggleEnabled={handleToggleEnabled}
      />
      {!copilotOpen && <CopilotLauncher onOpen={() => setCopilotOpen(true)} />}
      <CopilotPanel
        open={copilotOpen}
        onClose={() => setCopilotOpen(false)}
        system={lang === 'en' ? `You explain this material and its processing status. Focus on the current material id: ${currentMaterial._id}. Use tools for facts.` : `你解释该材料及其处理状态。关注当前材料ID：${currentMaterial._id}，使用工具提供事实。`}
        tools={['material_get', 'material_status']}
      />
    </>
  );
}

export default MaterialDetailPage; 
